import { useState, useRef, useEffect, useCallback } from 'react'
import { useUIStore } from '@/stores'
import { useProject, useFolders, useThreads, useCreateFolder, useCreateThread, useDeleteProject, useDeleteThread, useArchiveThread, useAvailableModels } from '@/hooks'
import { FolderPlus, MessageSquarePlus, Trash2, Archive, ChevronRight, ChevronDown, Folder, MessageSquare, Pin, Code, Terminal, Layout, Bot, FileText, List, LayoutGrid, AlignJustify, Plus, X, Upload, FileUp, FolderUp, CheckCircle, AlertCircle, Loader2, Clock, XCircle, Eye, Download, Pencil, Move } from 'lucide-react'
import { formatDate, getModelShortName } from '@/utils/helpers'
import { parseTags } from '@/types'
import type { Folder as FolderType, Thread } from '@/types'
import { useT } from '@/i18n'
import { api } from '@/services/api-client'

type SortBy = 'name' | 'created' | 'modified'
type ViewMode = 'tree' | 'list' | 'icons'

type ImportStatus = 'pending' | 'uploading' | 'done' | 'error'
interface ImportItem { id: number; file: File; status: ImportStatus; progress: number; error?: string }

function fmtSize(b: number) {
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${(b / 1024 / 1024).toFixed(2)} MB`
}

const API_BASE = (import.meta as any).env?.VITE_API_URL || ((import.meta as any).env?.PROD ? '' : 'http://localhost:3001')

function uploadOneFile(file: File, projectId: string | null, folderId: string | null, onProgress: (p: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const token = localStorage.getItem('synapsios_token')
    const xhr = new XMLHttpRequest()
    const fd = new FormData()
    fd.append('files', file)
    if (projectId) fd.append('project_id', projectId)
    if (folderId) fd.append('folder_id', folderId)
    xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress(Math.round(e.loaded / e.total * 100)) }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) { resolve(); return }
      try { reject(new Error(JSON.parse(xhr.responseText).error || `HTTP ${xhr.status}`)) }
      catch { reject(new Error(`HTTP ${xhr.status}`)) }
    }
    xhr.onerror = () => reject(new Error('Błąd sieci'))
    xhr.open('POST', `${API_BASE}/api/files/upload`)
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.send(fd)
  })
}

function getFileMimeIcon(mime: string) {
  if (mime.startsWith('image/')) return <span style={{ fontSize: 11 }}>🖼</span>
  if (mime === 'application/pdf') return <span style={{ fontSize: 11 }}>📄</span>
  if (mime.startsWith('text/') || mime.includes('javascript') || mime.includes('typescript') || mime.includes('json') || mime.includes('xml') || mime.includes('yaml') || mime.includes('sql'))
    return <Code size={13} className="thread-icon" />
  return <FileText size={13} className="thread-icon" />
}

function isViewable(mime: string): boolean {
  return mime.startsWith('image/') || mime.startsWith('text/') ||
    ['application/json', 'application/xml', 'application/javascript', 'application/typescript',
     'application/x-yaml', 'application/x-sh', 'application/sql', 'application/graphql',
     'application/ld+json', 'application/manifest+json'].some(p => mime.startsWith(p))
}

function getStoredViewMode(): ViewMode {
  try { return (localStorage.getItem('explorer-view') as ViewMode) || 'tree' } catch { return 'tree' }
}

export function ProjectExplorer() {
  const t = useT()
  const { activeProjectId, navigateToThread, navigateToDashboard } = useUIStore()
  const { data: project } = useProject(activeProjectId)
  const { data: folders } = useFolders(activeProjectId)
  const { data: threads } = useThreads(activeProjectId)
  const createFolder = useCreateFolder()
  const createThread = useCreateThread()
  const deleteProject = useDeleteProject()
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const { data: availableModels } = useAvailableModels()
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [showNewThread, setShowNewThread] = useState(false)
  const [newName, setNewName] = useState('')
  const [newThreadFolder, setNewThreadFolder] = useState<string | null>(null)
  const [newThreadProvider, setNewThreadProvider] = useState('')
  const [newThreadModel, setNewThreadModel] = useState('')
  const [newThreadLanguage, setNewThreadLanguage] = useState('')
  const deleteThread = useDeleteThread()
  const archiveThread = useArchiveThread()
  const [sortBy, setSortBy] = useState<SortBy>('name')
  const [viewMode, setViewMode] = useState<ViewMode>(getStoredViewMode)
  const [showImportMenu, setShowImportMenu] = useState(false)
  const [importQueue, setImportQueue] = useState<ImportItem[]>([])
  const [showImportPanel, setShowImportPanel] = useState(false)
  const [projectFiles, setProjectFiles] = useState<import('@/types').UserFile[]>([])
  const [viewerFileId, setViewerFileId] = useState<string | null>(null)
  const [movingFileId, setMovingFileId] = useState<string | null>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const importMenuRef = useRef<HTMLDivElement>(null)
  const importAbort = useRef(false)

  const loadProjectFiles = useCallback(async () => {
    if (!activeProjectId) return
    try { setProjectFiles(await api.getProjectFiles(activeProjectId)) } catch { /* */ }
  }, [activeProjectId])

  useEffect(() => { loadProjectFiles() }, [loadProjectFiles])

  useEffect(() => {
    if (folderInputRef.current) folderInputRef.current.setAttribute('webkitdirectory', '')
  }, [])

  useEffect(() => {
    if (!showImportMenu) return
    const handler = (e: MouseEvent) => {
      if (importMenuRef.current && !importMenuRef.current.contains(e.target as Node)) setShowImportMenu(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showImportMenu])

  const handleImport = async (fileList: FileList) => {
    if (!fileList.length) return
    setShowImportMenu(false)
    importAbort.current = false
    const fileArray = Array.from(fileList)
    const items: ImportItem[] = fileArray.map((file, id) => ({ id, file, status: 'pending' as const, progress: 0 }))
    setImportQueue(items)
    setShowImportPanel(true)

    // Build folder structure from webkitRelativePath
    const pathToFolderId: Record<string, string> = {}
    const hasStructure = fileArray.some(f => (f as any).webkitRelativePath?.includes('/'))
    if (hasStructure && activeProjectId) {
      // Collect unique directory paths
      const dirPaths = new Set<string>()
      for (const file of fileArray) {
        const rel: string = (file as any).webkitRelativePath || ''
        if (!rel.includes('/')) continue
        const parts = rel.split('/')
        for (let d = 1; d < parts.length; d++) dirPaths.add(parts.slice(0, d).join('/'))
      }
      // Sort by depth so parents are created first
      const sorted = [...dirPaths].sort((a, b) => a.split('/').length - b.split('/').length)
      for (const dirPath of sorted) {
        const parts = dirPath.split('/')
        const parentPath = parts.length > 1 ? parts.slice(0, -1).join('/') : null
        const parentFolderId = parentPath ? pathToFolderId[parentPath] || null : null
        try {
          const folder = await api.createFolder({ project_id: activeProjectId, parent_folder_id: parentFolderId, name: parts[parts.length - 1] })
          pathToFolderId[dirPath] = folder.id
        } catch { /* ignore duplicate */ }
      }
    }

    for (let i = 0; i < items.length; i++) {
      if (importAbort.current) break
      setImportQueue(prev => prev.map(it => it.id === i ? { ...it, status: 'uploading', progress: 0 } : it))
      try {
        // Determine target folder from webkitRelativePath
        let folderId: string | null = null
        const rel: string = (fileArray[i] as any).webkitRelativePath || ''
        if (rel.includes('/') && hasStructure) {
          const parts = rel.split('/')
          const dirPath = parts.slice(0, -1).join('/')
          folderId = pathToFolderId[dirPath] || null
        }
        await uploadOneFile(items[i].file, activeProjectId, folderId, progress => {
          setImportQueue(prev => prev.map(it => it.id === i ? { ...it, progress } : it))
        })
        setImportQueue(prev => prev.map(it => it.id === i ? { ...it, status: 'done', progress: 100 } : it))
      } catch (e: any) {
        setImportQueue(prev => prev.map(it => it.id === i ? { ...it, status: 'error', error: e.message } : it))
      }
    }
    loadProjectFiles()
  }

  const setView = (mode: ViewMode) => {
    setViewMode(mode)
    try { localStorage.setItem('explorer-view', mode) } catch { /* */ }
  }

  const selectedProviderModels = availableModels?.find(p => p.provider_type === newThreadProvider)?.models || []

  const sortItems = <T extends { name?: string; title?: string; created_at?: string; updated_at?: string }>(items: T[]): T[] => {
    return [...items].sort((a, b) => {
      if (sortBy === 'name') return (a.name || a.title || '').localeCompare(b.name || b.title || '')
      if (sortBy === 'created') return (b.created_at || '').localeCompare(a.created_at || '')
      if (sortBy === 'modified') return (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || '')
      return 0
    })
  }

  if (!project || !activeProjectId) return <div className="empty-state"><p>{t.projSelect}</p></div>

  const rootFolders = sortItems(folders?.filter((f) => !f.parent_folder_id) || [])
  const rootThreads = sortItems(threads?.filter((t) => !t.folder_id) || [])

  const toggleFolder = (id: string) => {
    const next = new Set(expandedFolders)
    if (next.has(id)) next.delete(id); else next.add(id)
    setExpandedFolders(next)
  }

  const handleCreateFolder = (parentId?: string) => {
    if (!newName.trim()) return
    createFolder.mutate({ project_id: activeProjectId, parent_folder_id: parentId || null, name: newName.trim() }, {
      onSuccess: () => { setNewName(''); setShowNewFolder(false) },
    })
  }

  const handleCreateThread = (folderId?: string) => {
    if (!newName.trim()) return
    createThread.mutate({
      project_id: activeProjectId,
      folder_id: folderId || null,
      title: newName.trim(),
      default_provider: newThreadProvider || undefined,
      default_model: newThreadModel || undefined,
      language: newThreadLanguage || undefined,
    }, {
      onSuccess: () => { setNewName(''); setShowNewThread(false); setNewThreadFolder(null); setNewThreadProvider(''); setNewThreadModel(''); setNewThreadLanguage('') },
    })
  }

  const handleDeleteProject = () => {
    if (confirm(`Delete project "${project.name}" and all its data?`)) {
      deleteProject.mutate(activeProjectId, { onSuccess: navigateToDashboard })
    }
  }

  const tags = parseTags(project.tags)

  return (
    <div className="project-explorer">
      <div className="explorer-header">
        <div className="explorer-title-row">
          <div className="explorer-color-dot" style={{ backgroundColor: project.icon_color }} />
          <h1>{project.name}</h1>
          <span className={`status-badge status-${project.status}`}>{project.status}</span>
        </div>
        <div className="explorer-actions">
          <select className="explorer-sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value as SortBy)} title="Sort by">
            <option value="name">A-Z</option>
            <option value="created">Created</option>
            <option value="modified">Modified</option>
          </select>
          <div className="explorer-view-toggle">
            <button className={`explorer-view-btn${viewMode === 'tree' ? ' active' : ''}`} onClick={() => setView('tree')} title="Tree view"><AlignJustify size={14} /></button>
            <button className={`explorer-view-btn${viewMode === 'list' ? ' active' : ''}`} onClick={() => setView('list')} title="List view"><List size={14} /></button>
            <button className={`explorer-view-btn${viewMode === 'icons' ? ' active' : ''}`} onClick={() => setView('icons')} title="Icons view"><LayoutGrid size={14} /></button>
          </div>
          <button className="button-primary" onClick={() => { setShowNewFolder(true); setShowNewThread(false); setNewName('') }}>
            <FolderPlus size={14} /> {t.projNewFolder}
          </button>
          <button className="button-primary" onClick={() => { setShowNewThread(true); setShowNewFolder(false); setNewName(''); setNewThreadFolder(null) }}>
            <MessageSquarePlus size={14} /> {t.projNewThread}
          </button>

          {/* Import button */}
          <div className="explorer-import-wrap" ref={importMenuRef}>
            <button
              className="button-secondary explorer-import-btn"
              onClick={() => setShowImportMenu(v => !v)}
              title="Importuj pliki lub folder"
            >
              <Upload size={14} /> Import
            </button>
            {showImportMenu && (
              <div className="explorer-import-menu">
                <label className="explorer-import-option">
                  <input type="file" multiple style={{ display: 'none' }}
                    onChange={e => { if (e.target.files?.length) handleImport(e.target.files); e.target.value = '' }} />
                  <FileUp size={14} /> Pliki
                </label>
                <label className="explorer-import-option">
                  <input ref={folderInputRef} type="file" multiple style={{ display: 'none' }}
                    onChange={e => { if (e.target.files?.length) handleImport(e.target.files); e.target.value = '' }} />
                  <FolderUp size={14} /> Folder
                </label>
              </div>
            )}
          </div>

          <button className="button-danger" onClick={handleDeleteProject} title={t.projDeleteProject}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Import progress panel */}
      {showImportPanel && importQueue.length > 0 && (() => {
        const done = importQueue.filter(i => i.status === 'done').length
        const errors = importQueue.filter(i => i.status === 'error').length
        const total = importQueue.length
        const current = importQueue.find(i => i.status === 'uploading')
        const finished = done + errors === total
        const overallPct = Math.round((done + errors) / total * 100)
        return (
          <div className="imp-panel">
            <div className="imp-panel-header">
              <span className="imp-panel-title">
                {finished
                  ? `Import zakończony — ${done}/${total} ${errors > 0 ? `(${errors} błędów)` : ''}`
                  : `Importowanie ${done + errors + 1}/${total}…`}
              </span>
              <button className="imp-panel-close" onClick={() => { importAbort.current = true; setShowImportPanel(false) }}><X size={13} /></button>
            </div>

            {/* Overall bar */}
            <div className="imp-overall">
              <div className="imp-bar"><div className="imp-bar-fill" style={{ width: `${overallPct}%` }} /></div>
              <span className="imp-pct">{overallPct}%</span>
            </div>

            {/* Current file */}
            {current && (
              <div className="imp-current">
                <div className="imp-current-name"><Loader2 size={12} className="spinner" /> {current.file.name}</div>
                <div className="imp-bar imp-bar-file"><div className="imp-bar-fill imp-bar-fill-accent" style={{ width: `${current.progress}%` }} /></div>
                <div className="imp-current-meta">{current.progress}% · {fmtSize(current.file.size)}</div>
              </div>
            )}

            {/* Queue list */}
            <div className="imp-queue">
              {importQueue.map(item => (
                <div key={item.id} className={`imp-item imp-item-${item.status}`}>
                  {item.status === 'done' && <CheckCircle size={11} />}
                  {item.status === 'error' && <XCircle size={11} />}
                  {item.status === 'uploading' && <Loader2 size={11} className="spinner" />}
                  {item.status === 'pending' && <Clock size={11} />}
                  <span className="imp-item-name" title={item.file.name}>{item.file.name}</span>
                  <span className="imp-item-size">{fmtSize(item.file.size)}</span>
                  {item.status === 'uploading' && <span className="imp-item-pct">{item.progress}%</span>}
                  {item.error && <span className="imp-item-err" title={item.error}>!</span>}
                </div>
              ))}
            </div>
          </div>
        )
      })()}

      {project.description && <p className="explorer-description">{project.description}</p>}

      <div className="explorer-meta">
        <span>Created {formatDate(project.created_at)}</span>
        <span>{project.thread_count || 0} threads</span>
        <span>{project.folder_count || 0} folders</span>
        <span>Priority: {project.priority}</span>
      </div>

      {tags.length > 0 && (
        <div className="explorer-tags">
          {tags.map((tag) => <span key={tag} className="tag">{tag}</span>)}
        </div>
      )}

      {(showNewFolder || showNewThread) && (
        <div className="explorer-inline-form">
          {showNewThread && newThreadFolder && (
            <div className="explorer-form-context">
              <Folder size={12} />
              <span>{folders?.find(f => f.id === newThreadFolder)?.name || 'Folder'}</span>
              <button onClick={() => setNewThreadFolder(null)} title="Dodaj do głównego katalogu"><X size={10} /></button>
            </div>
          )}
          <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') showNewFolder ? handleCreateFolder() : handleCreateThread(newThreadFolder || undefined)
              if (e.key === 'Escape') { setShowNewFolder(false); setShowNewThread(false); setNewThreadProvider(''); setNewThreadModel(''); setNewThreadLanguage('') }
            }}
            placeholder={showNewFolder ? t.projFolderName : t.projThreadTitle}
            className="explorer-inline-input"
          />
          {showNewThread && availableModels && availableModels.length > 0 && (
            <div className="explorer-model-selectors">
              <select
                className="explorer-inline-select"
                value={newThreadProvider}
                onChange={(e) => {
                  setNewThreadProvider(e.target.value)
                  if (!e.target.value) { setNewThreadModel(''); return }
                  const models = availableModels.find(p => p.provider_type === e.target.value)?.models || []
                  setNewThreadModel(models[0] || '')
                }}
              >
                <option value="">Notatki (bez AI)</option>
                {availableModels.map(p => (
                  <option key={p.provider_type} value={p.provider_type}>{p.display_name}</option>
                ))}
              </select>
              {newThreadProvider && selectedProviderModels.length > 0 && (
                <select
                  className="explorer-inline-select"
                  value={newThreadModel}
                  onChange={(e) => setNewThreadModel(e.target.value)}
                >
                  {selectedProviderModels.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              )}
            </div>
          )}
          {showNewThread && (
            <div className="explorer-model-selectors" style={{ marginTop: 4 }}>
              <select
                className="explorer-inline-select"
                value={newThreadLanguage}
                onChange={(e) => setNewThreadLanguage(e.target.value)}
              >
                <option value="">{t.threadSelectLanguage}</option>
                <option value="English">English</option>
                <option value="Polish">Polski</option>
                <option value="Slovak">Slovensky</option>
                <option value="German">Deutsch</option>
                <option value="French">Français</option>
                <option value="Spanish">Español</option>
                <option value="Italian">Italiano</option>
                <option value="Portuguese">Português</option>
                <option value="Dutch">Nederlands</option>
                <option value="Russian">Русский</option>
                <option value="Ukrainian">Українська</option>
                <option value="Czech">Česky</option>
                <option value="Japanese">日本語</option>
                <option value="Chinese">中文</option>
                <option value="Korean">한국어</option>
                <option value="Arabic">العربية</option>
                <option value="Hindi">हिन्दी</option>
                <option value="Turkish">Türkçe</option>
                <option value="Swedish">Svenska</option>
                <option value="Norwegian">Norsk</option>
              </select>
            </div>
          )}
        </div>
      )}

      {viewMode === 'tree' && (
        <div className="explorer-tree">
          {rootFolders.map((folder) => (
            <ExplorerFolder
              key={folder.id}
              folder={folder}
              allFolders={folders || []}
              allThreads={threads || []}
              allFiles={projectFiles}
              projectId={activeProjectId}
              isExpanded={expandedFolders.has(folder.id)}
              onToggle={() => toggleFolder(folder.id)}
              expandedFolders={expandedFolders}
              onToggleFolder={toggleFolder}
              onSelectThread={(id: string) => navigateToThread(id, activeProjectId)}
              onArchiveThread={(id: string) => { if (confirm(t.threadArchiveConfirm)) archiveThread.mutate({ id, projectId: activeProjectId }) }}
              onDeleteThread={(id: string) => { if (confirm(t.threadDeleteConfirm)) deleteThread.mutate({ id, projectId: activeProjectId }) }}
              onAddThread={(folderId: string) => {
                setNewThreadFolder(folderId)
                setShowNewThread(true)
                setShowNewFolder(false)
                setNewName('')
                if (!expandedFolders.has(folderId)) toggleFolder(folderId)
              }}
              onOpenFile={(id) => setViewerFileId(id)}
              onDeleteFile={async (id) => {
                const f = projectFiles.find(x => x.id === id)
                if (!confirm(`Usunąć plik "${f?.name || id}"?`)) return
                await api.deleteFile(id); loadProjectFiles()
              }}
              onMoveFile={async (id, fid) => { await api.updateFile(id, { folder_id: fid }); loadProjectFiles() }}
              movingFileId={movingFileId}
              onToggleMoveFile={(id) => setMovingFileId(movingFileId === id ? null : id)}
            />
          ))}
          {rootThreads.map((thread) => (
            <ExplorerThread key={thread.id} thread={thread} onSelect={() => navigateToThread(thread.id, activeProjectId)}
              onArchive={() => { if (confirm(t.threadArchiveConfirm)) archiveThread.mutate({ id: thread.id, projectId: activeProjectId }) }}
              onDelete={() => { if (confirm(t.threadDeleteConfirm)) deleteThread.mutate({ id: thread.id, projectId: activeProjectId }) }}
            />
          ))}
          {rootFolders.length === 0 && rootThreads.length === 0 && (
            <div className="empty-state small"><p>{t.projEmpty}</p></div>
          )}
        </div>
      )}

      {viewMode === 'list' && (
        <ExplorerListView
          folders={folders || []}
          threads={sortItems(threads || [])}
          onSelectThread={(id) => navigateToThread(id, activeProjectId)}
          onArchiveThread={(id) => { if (confirm(t.threadArchiveConfirm)) archiveThread.mutate({ id, projectId: activeProjectId }) }}
          onDeleteThread={(id) => { if (confirm(t.threadDeleteConfirm)) deleteThread.mutate({ id, projectId: activeProjectId }) }}
          t={t}
        />
      )}

      {viewMode === 'icons' && (
        <ExplorerIconsView
          folders={folders || []}
          threads={sortItems(threads || [])}
          onSelectThread={(id) => navigateToThread(id, activeProjectId)}
          onArchiveThread={(id) => { if (confirm(t.threadArchiveConfirm)) archiveThread.mutate({ id, projectId: activeProjectId }) }}
          onDeleteThread={(id) => { if (confirm(t.threadDeleteConfirm)) deleteThread.mutate({ id, projectId: activeProjectId }) }}
          t={t}
        />
      )}

      {/* Project files - only show those without a folder (unassigned) */}
      {projectFiles.filter(f => !f.folder_id).length > 0 && (
        <div className="explorer-files-section">
          <div className="explorer-files-header">
            <FileText size={14} /> Pliki projektu
            <span className="explorer-item-count">{projectFiles.filter(f => !f.folder_id).length}</span>
            {projectFiles.length > 0 && (
              <a
                href={api.getProjectFilesDownloadUrl(activeProjectId)}
                className="explorer-files-download"
                title="Pobierz wszystkie pliki projektu (ZIP)"
                download
              >
                <Download size={12} />
              </a>
            )}
          </div>
          {projectFiles.filter(f => !f.folder_id).map(f => (
            <ExplorerFileItem
              key={f.id}
              file={f}
              folders={folders || []}
              onOpen={() => setViewerFileId(f.id)}
              onDelete={async () => {
                if (!confirm(`Usunąć plik "${f.name}"?`)) return
                await api.deleteFile(f.id)
                loadProjectFiles()
              }}
              onMove={async (targetFolderId) => {
                await api.updateFile(f.id, { folder_id: targetFolderId })
                loadProjectFiles()
              }}
              isMoving={movingFileId === f.id}
              onToggleMove={() => setMovingFileId(movingFileId === f.id ? null : f.id)}
            />
          ))}
        </div>
      )}

      {/* File viewer modal */}
      {viewerFileId && (
        <FileViewerModal
          fileId={viewerFileId}
          onClose={() => setViewerFileId(null)}
          onSaved={loadProjectFiles}
        />
      )}
    </div>
  )
}

function ExplorerFolder({ folder, allFolders, allThreads, allFiles, projectId, isExpanded, onToggle, expandedFolders, onToggleFolder, onSelectThread, onArchiveThread, onDeleteThread, onAddThread, onOpenFile, onDeleteFile, onMoveFile, movingFileId, onToggleMoveFile }: {
  folder: FolderType; allFolders: FolderType[]; allThreads: Thread[]
  allFiles: import('@/types').UserFile[]
  projectId: string
  isExpanded: boolean
  onToggle: () => void; expandedFolders: Set<string>; onToggleFolder: (id: string) => void; onSelectThread: (id: string) => void
  onArchiveThread: (id: string) => void; onDeleteThread: (id: string) => void; onAddThread?: (folderId: string) => void
  onOpenFile: (id: string) => void; onDeleteFile: (id: string) => void; onMoveFile: (id: string, folderId: string | null) => void
  movingFileId: string | null; onToggleMoveFile: (id: string) => void
}) {
  const subFolders = allFolders.filter((f) => f.parent_folder_id === folder.id)
  const folderThreads = allThreads.filter((t) => t.folder_id === folder.id)
  const folderFiles = allFiles.filter((f) => f.folder_id === folder.id)
  const isEmpty = subFolders.length === 0 && folderThreads.length === 0 && folderFiles.length === 0

  return (
    <div className="explorer-folder-node">
      <div className="explorer-item explorer-folder-item" onClick={onToggle}>
        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Folder size={16} className="folder-icon" />
        <span className="explorer-item-name">{folder.name}</span>
        <span className="explorer-item-count">{folderThreads.length + folderFiles.length}</span>
        <a
          className="explorer-action-btn explorer-folder-add"
          href={api.getProjectFilesDownloadUrl(projectId, folder.id)}
          title="Pobierz folder jako ZIP"
          download
          onClick={e => e.stopPropagation()}
        >
          <Download size={12} />
        </a>
        {onAddThread && (
          <button
            className="explorer-action-btn explorer-folder-add"
            title="Dodaj wątek w tym folderze"
            onClick={e => { e.stopPropagation(); onAddThread(folder.id) }}
          >
            <Plus size={12} />
          </button>
        )}
      </div>
      {isExpanded && (
        <div className="explorer-children">
          {subFolders.map((sf) => (
            <ExplorerFolder key={sf.id} folder={sf} allFolders={allFolders} allThreads={allThreads} allFiles={allFiles}
              projectId={projectId}
              isExpanded={expandedFolders.has(sf.id)} onToggle={() => onToggleFolder(sf.id)}
              expandedFolders={expandedFolders} onToggleFolder={onToggleFolder} onSelectThread={onSelectThread}
              onArchiveThread={onArchiveThread} onDeleteThread={onDeleteThread} onAddThread={onAddThread}
              onOpenFile={onOpenFile} onDeleteFile={onDeleteFile} onMoveFile={onMoveFile}
              movingFileId={movingFileId} onToggleMoveFile={onToggleMoveFile} />
          ))}
          {folderThreads.map((th) => (
            <ExplorerThread key={th.id} thread={th} onSelect={() => onSelectThread(th.id)}
              onArchive={() => onArchiveThread(th.id)} onDelete={() => onDeleteThread(th.id)} />
          ))}
          {folderFiles.map((f) => (
            <ExplorerFileItem key={f.id} file={f} folders={allFolders}
              onOpen={() => onOpenFile(f.id)}
              onDelete={() => onDeleteFile(f.id)}
              onMove={(fid) => onMoveFile(f.id, fid)}
              isMoving={movingFileId === f.id}
              onToggleMove={() => onToggleMoveFile(f.id)}
            />
          ))}
          {isEmpty && (
            <div className="explorer-empty-folder">
              Pusty folder ·{' '}
              {onAddThread && (
                <button className="explorer-empty-add" onClick={() => onAddThread(folder.id)}>
                  + Dodaj wątek
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  code: <Code size={14} className="thread-icon thread-cat-code" />,
  prompt: <Terminal size={14} className="thread-icon thread-cat-prompt" />,
  ui: <Layout size={14} className="thread-icon thread-cat-ui" />,
  agent: <Bot size={14} className="thread-icon thread-cat-agent" />,
  other: <FileText size={14} className="thread-icon thread-cat-other" />,
}

function ExplorerThread({ thread, onSelect, onArchive, onDelete }: { thread: Thread; onSelect: () => void; onArchive?: () => void; onDelete?: () => void }) {
  const t = useT()
  const catIcon = thread.thread_category ? CATEGORY_ICONS[thread.thread_category] : null
  return (
    <div className="explorer-item explorer-thread-item">
      <div className="explorer-thread-main" onClick={onSelect}>
        {catIcon || <MessageSquare size={14} className="thread-icon" />}
        <span className="explorer-item-name">{thread.title}</span>
        {thread.pinned === 1 && <Pin size={10} className="pin-icon" />}
        <span className="explorer-item-model">{getModelShortName(thread.default_model)}</span>
        {thread.message_count !== undefined && <span className="explorer-item-count">{thread.message_count} {t.msgs}</span>}
      </div>
      {(onArchive || onDelete) && (
        <div className="explorer-thread-actions">
          {onArchive && (
            <button className="explorer-action-btn" onClick={(e) => { e.stopPropagation(); onArchive() }} title={t.threadArchive}>
              <Archive size={12} />
            </button>
          )}
          {onDelete && (
            <button className="explorer-action-btn explorer-action-danger" onClick={(e) => { e.stopPropagation(); onDelete() }} title={t.delete}>
              <Trash2 size={12} />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function ExplorerFileItem({ file, folders, onOpen, onDelete, onMove, isMoving, onToggleMove }: {
  file: import('@/types').UserFile
  folders: FolderType[]
  onOpen: () => void
  onDelete: () => void
  onMove: (folderId: string | null) => void
  isMoving: boolean
  onToggleMove: () => void
}) {
  const canView = isViewable(file.mime_type)
  return (
    <div className="explorer-item explorer-file-item">
      {getFileMimeIcon(file.mime_type)}
      <span className="explorer-item-name" title={file.name}
        onClick={canView ? onOpen : undefined}
        style={canView ? { cursor: 'pointer' } : undefined}
      >{file.name}</span>
      <span className="explorer-item-size">{fmtSize(file.size)}</span>
      <div className="explorer-file-actions">
        {canView && (
          <button className="explorer-action-btn" onClick={onOpen} title="Otwórz">
            <Eye size={12} />
          </button>
        )}
        <div className="explorer-move-wrap" style={{ position: 'relative' }}>
          <button className="explorer-action-btn" onClick={onToggleMove} title="Przenieś do folderu">
            <Move size={12} />
          </button>
          {isMoving && (
            <div className="explorer-move-menu" onClick={e => e.stopPropagation()}>
              <div className="explorer-move-item" onClick={() => { onMove(null); onToggleMove() }}>
                — Bez folderu
              </div>
              {folders.map(f => (
                <div key={f.id} className="explorer-move-item" onClick={() => { onMove(f.id); onToggleMove() }}>
                  <Folder size={11} /> {f.name}
                </div>
              ))}
            </div>
          )}
        </div>
        <button className="explorer-action-btn explorer-action-danger" onClick={onDelete} title="Usuń">
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  )
}

function FileViewerModal({ fileId, onClose, onSaved }: { fileId: string; onClose: () => void; onSaved: () => void }) {
  const [file, setFile] = useState<import('@/types').UserFile | null>(null)
  const [content, setContent] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [edited, setEdited] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    api.getFileContent(fileId).then(f => {
      setFile(f)
      setContent(f.content || '')
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [fileId])

  const handleSave = async () => {
    if (!file || !edited) return
    setSaving(true)
    try {
      await api.updateFile(file.id, { content })
      setEdited(false)
      onSaved()
    } finally {
      setSaving(false)
    }
  }

  const isImage = file?.mime_type?.startsWith('image/')
  const isText = file ? isViewable(file.mime_type) && !isImage : false

  return (
    <div className="viewer-overlay" onClick={onClose}>
      <div className="viewer-modal" onClick={e => e.stopPropagation()}>
        <div className="viewer-header">
          <span className="viewer-name">{file?.name || '…'}</span>
          {file && <span className="viewer-meta">{fmtSize(file.size)} · {file.mime_type}</span>}
          <div className="viewer-header-actions">
            {isText && edited && (
              <button className="button-primary viewer-save-btn" onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 size={13} className="spinner" /> : <CheckCircle size={13} />} Zapisz
              </button>
            )}
            <button className="explorer-action-btn" onClick={onClose} title="Zamknij"><X size={16} /></button>
          </div>
        </div>
        <div className="viewer-body">
          {loading && <div className="viewer-loading"><Loader2 size={24} className="spinner" /></div>}
          {!loading && isImage && (
            <div className="viewer-image-wrap">
              <img src={`data:${file!.mime_type};base64,${content}`} alt={file!.name} className="viewer-image" />
            </div>
          )}
          {!loading && isText && (
            <textarea
              className="viewer-code"
              value={content}
              onChange={e => { setContent(e.target.value); setEdited(true) }}
              spellCheck={false}
            />
          )}
          {!loading && !loading && !isImage && !isText && file && (
            <div className="viewer-binary">
              <FileText size={40} />
              <p>{file.name}</p>
              <p className="viewer-binary-meta">{fmtSize(file.size)} · {file.mime_type}</p>
              <p>Podgląd tego pliku nie jest dostępny w przeglądarce.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

type ViewProps = {
  folders: FolderType[]
  threads: Thread[]
  onSelectThread: (id: string) => void
  onArchiveThread: (id: string) => void
  onDeleteThread: (id: string) => void
  t: ReturnType<typeof useT>
}

function getFolderName(folders: FolderType[], folderId: string | null | undefined): string {
  if (!folderId) return ''
  return folders.find(f => f.id === folderId)?.name || ''
}

function ExplorerListView({ folders, threads, onSelectThread, onArchiveThread, onDeleteThread, t }: ViewProps) {
  if (threads.length === 0 && folders.length === 0) {
    return <div className="empty-state small"><p>{t.projEmpty}</p></div>
  }
  return (
    <div className="explorer-list-view">
      <div className="explorer-list-header">
        <span className="elh-name">{t.projThreadTitle || 'Title'}</span>
        <span className="elh-folder">Folder</span>
        <span className="elh-model">Model</span>
        <span className="elh-msgs">{t.msgs}</span>
        <span className="elh-date">Modified</span>
        <span className="elh-actions" />
      </div>
      {folders.map(folder => (
        <div key={folder.id} className="explorer-list-row explorer-list-folder">
          <span className="elr-icon"><Folder size={15} className="folder-icon" /></span>
          <span className="elr-name">{folder.name}</span>
          <span className="elr-folder">{getFolderName(folders, folder.parent_folder_id)}</span>
          <span className="elr-model" />
          <span className="elr-msgs" />
          <span className="elr-date">{folder.created_at ? formatDate(folder.created_at) : ''}</span>
          <span className="elr-actions" />
        </div>
      ))}
      {threads.map(thread => {
        const catIcon = thread.thread_category ? CATEGORY_ICONS[thread.thread_category] : <MessageSquare size={14} className="thread-icon" />
        return (
          <div key={thread.id} className="explorer-list-row explorer-list-thread" onClick={() => onSelectThread(thread.id)}>
            <span className="elr-icon">{catIcon}</span>
            <span className="elr-name">
              {thread.title}
              {thread.pinned === 1 && <Pin size={10} className="pin-icon" style={{ marginLeft: 4 }} />}
            </span>
            <span className="elr-folder">{getFolderName(folders, thread.folder_id)}</span>
            <span className="elr-model">{getModelShortName(thread.default_model)}</span>
            <span className="elr-msgs">{thread.message_count ?? ''}</span>
            <span className="elr-date">{thread.updated_at ? formatDate(thread.updated_at) : thread.created_at ? formatDate(thread.created_at) : ''}</span>
            <span className="elr-actions" onClick={e => e.stopPropagation()}>
              <button className="explorer-action-btn" onClick={() => onArchiveThread(thread.id)} title={t.threadArchive}><Archive size={12} /></button>
              <button className="explorer-action-btn explorer-action-danger" onClick={() => onDeleteThread(thread.id)} title={t.delete}><Trash2 size={12} /></button>
            </span>
          </div>
        )
      })}
    </div>
  )
}

function ExplorerIconsView({ folders, threads, onSelectThread, onArchiveThread, onDeleteThread, t }: ViewProps) {
  if (threads.length === 0 && folders.length === 0) {
    return <div className="empty-state small"><p>{t.projEmpty}</p></div>
  }
  return (
    <div className="explorer-icons-view">
      {folders.map(folder => (
        <div key={folder.id} className="explorer-icon-item explorer-icon-folder">
          <div className="explorer-icon-img"><Folder size={36} className="folder-icon" /></div>
          <span className="explorer-icon-name">{folder.name}</span>
        </div>
      ))}
      {threads.map(thread => {
        const catIcon = thread.thread_category ? CATEGORY_ICONS[thread.thread_category] : null
        return (
          <div key={thread.id} className="explorer-icon-item explorer-icon-thread" onClick={() => onSelectThread(thread.id)}>
            <div className="explorer-icon-img">
              {catIcon
                ? <span className="explorer-icon-cat">{catIcon}</span>
                : <MessageSquare size={32} className="thread-icon" />}
            </div>
            <span className="explorer-icon-name">{thread.title}</span>
            <div className="explorer-icon-actions" onClick={e => e.stopPropagation()}>
              <button className="explorer-action-btn" onClick={() => onArchiveThread(thread.id)} title={t.threadArchive}><Archive size={11} /></button>
              <button className="explorer-action-btn explorer-action-danger" onClick={() => onDeleteThread(thread.id)} title={t.delete}><Trash2 size={11} /></button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
